import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { Grid, Tooltip } from "@material-ui/core";
import StatusFilterHeader from "@View/components/table/StatusFilterHeader";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DataTable from "@View/components/table/DataTable";
import { selectCustomerId, selectCustomerRole } from "@State/selectors";
import ConfirmModal from "@View/components/ConfirmModal";
import LeadDetailsModal from "../modals/LeadDetailsModal";
import EditLeadModal from "../modals/EditLeadModal";
import NewLeadModal from "../modals/NewLeadModal";
import PrecreditReportModal from "../modals/PrecreditReportModal";
import PublicRatingModal from "../modals/PublicRatingModal";
import StopFactorsModal from "../modals/StopFactorsModal";
import ButtonAddNew from "@View/components/buttons/ButtonAddNew";
import ViewIcon from "@View/components/svgComponents/ViewIcon";
import EditIcon from "@View/components/svgComponents/EditIcon";
import DetailsIcon from "@View/components/svgComponents/DetailsIcon";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import MoveDownIcon from "@mui/icons-material/MoveDown";
import { useTooltipStyle } from "@View/style/components/tooltipStyles";
import { getPointColorFromString } from "@Utils/colorPicker";
import getAPI from "@API/index";
import { getLeadRoleConfig } from "../leadRoleConfig";
import LeadStatusBadge from "../components/LeadStatusBadge";
import StopFactorIndicator from "../components/StopFactorIndicator";

const mapStateToProps = (state) => ({
  customerId: selectCustomerId(state),
  customerRole: selectCustomerRole(state),
});

const LeadsTable = ({ customerId, customerRole }) => {
  const routerHistory = useHistory();
  const { t } = useTranslation();
  const tooltipStyle = useTooltipStyle();

  const roleConfig = getLeadRoleConfig(customerRole);
  const { actions: actionConfig, columns: visibleColumns, showNewLeadButton } = roleConfig.leadsTable;

  const LEAD_STATUSES = ["New", "ReportGenerated", "SentToBank", "BankResponseReceived", "Archived"];

  const [leads, setLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [deletedId, setDeletedId] = useState();
  const [detailsLead, setDetailsLead] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [reportLead, setReportLead] = useState(null);
  const [ratingLead, setRatingLead] = useState(null);
  const [stopFactorsLead, setStopFactorsLead] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null); // { leadId, transferId }

  const filteredLeads = statusFilter.length === 0
    ? leads
    : leads.filter((l) => statusFilter.includes(l.status));

  useEffect(() => {
    if (!customerId) return;
    getAPI()
      .request({
        method: "get",
        url: "/api/lead",
        params: { customerId },
        suppressError: false,
      })
      .then((response) => {
        if (response?.data) setLeads(response.data);
      });
  }, [customerId]);

  const handleCloseModal = (event) => {
    event.preventDefault();
    setDeletedId();
  };

  const handleDelete = (event) => {
    event.preventDefault();
    getAPI()
      .request({
        method: "put",
        url: `/api/lead/${deletedId}/archive`,
        suppressError: false,
      })
      .then(() => {
        setLeads((prev) =>
          prev.map((l) => l.id === deletedId ? { ...l, status: "Archived" } : l)
        );
        setDeletedId();
      });
  };

  const handleConvert = (event) => {
    event.preventDefault();
    const { leadId, transferId } = convertTarget;
    getAPI()
      .request({
        method: "post",
        url: `/api/lead/${leadId}/convert`,
        params: { transferId },
        suppressError: false,
      })
      .then(() => {
        setConvertTarget(null);
      });
  };

  const allColumns = [
    {
      field: "borrowerName",
      headerName: t("inputs.CompanyName"),
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "inn",
      headerName: t("inputs.INN"),
      width: 150,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "requestedAmount",
      headerName: t("inputs.RequestedAmount"),
      width: 170,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) =>
        cell.row.requestedAmount != null ? cell.row.requestedAmount : "—",
    },
    {
      field: "requestedTermMonths",
      headerName: t("inputs.TermMonths"),
      width: 130,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) =>
        cell.row.requestedTermMonths != null ? cell.row.requestedTermMonths : "—",
    },
    {
      field: "stopFactors",
      headerName: t("inputs.StopFactors"),
      width: 140,
      headerAlign: "left",
      align: "left",
      cellClassName: "tbl-align-left",
      renderCell: (cell) => (
        <StopFactorIndicator
          count={cell.row.triggeredStopFactors}
          onClick={() => setStopFactorsLead(cell.row)}
        />
      ),
    },
    {
      field: "rating",
      headerName: "Рейтинг",
      width: 100,
      headerAlign: "left",
      align: "left",
      cellClassName: "tbl-align-left",
      renderCell: (cell) => {
        const color = getPointColorFromString(cell.row.ratingColor || "gray");
        return (
          <Grid container alignItems="center" style={{ gap: 6 }}>
            <div
              onClick={(e) => { e.preventDefault(); setRatingLead(cell.row); }}
              style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
            >
              <FiberManualRecordIcon style={{ color, fontSize: 24 }} />
            </div>
          </Grid>
        );
      },
    },
    {
      field: "status",
      headerName: t("inputs.Status"),
      width: 190,
      headerAlign: "left",
      align: "left",
      renderHeader: () => (
        <StatusFilterHeader
          label={t("inputs.Status")}
          statuses={LEAD_STATUSES}
          value={statusFilter}
          onChange={setStatusFilter}
          renderBadge={(s) => <LeadStatusBadge status={s} label={t(`LeadStatus.${s}`)} />}
        />
      ),
      renderCell: (cell) => <LeadStatusBadge status={cell.row.status} label={t(`LeadStatus.${cell.row.status}`)} />,
    },
    {
      field: "actions",
      headerName: " ",
      flex: 1,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => (
        <div className="tableIcons">
          {actionConfig.showRequests && (
            <Tooltip title={t("buttons.Requests")} classes={tooltipStyle}>
              <div
                onClick={(e) => {
                  e.preventDefault();
                  routerHistory.push(`/lead/${cell.row.id}`);
                }}
                className={tooltipStyle.element}
              >
                <AssignmentIcon style={{ fontSize: 20 }} />
              </div>
            </Tooltip>
          )}
          {actionConfig.showView && (
            <Tooltip title={t("buttons.View")} classes={tooltipStyle}>
              <div
                onClick={(e) => {
                  e.preventDefault();
                  routerHistory.push(`/lead/${cell.row.id}?section=Requests`);
                }}
                className={tooltipStyle.element}
              >
                <ViewIcon />
              </div>
            </Tooltip>
          )}
          {actionConfig.showReport && (
            <Tooltip title={t("buttons.Report")} classes={tooltipStyle}>
              <div
                onClick={(e) => {
                  e.preventDefault();
                  setReportLead(cell.row);
                }}
                className={tooltipStyle.element}
              >
                <AssessmentIcon style={{ fontSize: 20 }} />
              </div>
            </Tooltip>
          )}
          {actionConfig.showEdit && (
            <Tooltip title={t("buttons.Edit")} classes={tooltipStyle}>
              <div
                onClick={(e) => {
                  if (cell.row.status === "Archived") return;
                  e.preventDefault();
                  setEditLead(cell.row);
                }}
                className={tooltipStyle.element}
                style={cell.row.status === "Archived" ? { opacity: 0.3, cursor: "default", pointerEvents: "none" } : {}}
              >
                <EditIcon />
              </div>
            </Tooltip>
          )}
          {actionConfig.showDetails && (
            <Tooltip title={t("buttons.Details")} classes={tooltipStyle}>
              <div
                onClick={(e) => {
                  e.preventDefault();
                  setDetailsLead(cell.row);
                }}
                className={tooltipStyle.element}
              >
                <DetailsIcon />
              </div>
            </Tooltip>
          )}
          {actionConfig.showDelete && (
            <Tooltip title={t("buttons.Archive")} classes={tooltipStyle}>
              <div
                onClick={(e) => {
                  if (cell.row.status === "Archived") return;
                  e.preventDefault();
                  setDeletedId(cell.row.id);
                }}
                className={tooltipStyle.element}
                style={cell.row.status === "Archived" ? { opacity: 0.3, cursor: "default", pointerEvents: "none" } : {}}
              >
                <ArchiveOutlinedIcon style={{ fontSize: 20 }} />
              </div>
            </Tooltip>
          )}
          {actionConfig.showConvert && (() => {
            const canConvert = cell.row.canBeConverted;
            const transfer = (cell.row.transfers || []).find(
              (tr) => tr.leadReceiverCustomerId === customerId && tr.status === "CreditIssued"
            );
            return (
              <Tooltip title={t("buttons.Convert")} classes={tooltipStyle}>
                <div
                  onClick={(e) => {
                    if (!canConvert) return;
                    e.preventDefault();
                    setConvertTarget({ leadId: cell.row.id, transferId: transfer?.id ?? null });
                  }}
                  className={tooltipStyle.element}
                  style={!canConvert ? { opacity: 0.3, cursor: "default", pointerEvents: "none" } : {}}
                >
                  <MoveDownIcon style={{ fontSize: 20 }} />
                </div>
              </Tooltip>
            );
          })()}
        </div>
      ),
    },
  ];

  const columns = allColumns.filter((col) => visibleColumns.includes(col.field));

  return (
    <React.Fragment>
      <DataTable
        rows={filteredLeads}
        columns={columns}
        toolbarButtons={
          showNewLeadButton
            ? <ButtonAddNew onClick={(e) => { e.preventDefault(); setNewLeadOpen(true); }} label={t("buttons.Add New")} />
            : null
        }
      />
      <NewLeadModal
        customerId={customerId}
        open={newLeadOpen}
        handleClose={() => setNewLeadOpen(false)}
        onCreated={(created) => setLeads((prev) => [created, ...prev])}
      />
      <LeadDetailsModal
        lead={detailsLead}
        open={detailsLead !== null}
        handleClose={() => setDetailsLead(null)}
      />
      <PrecreditReportModal
        lead={reportLead}
        open={reportLead !== null}
        handleClose={() => setReportLead(null)}
      />
      <PublicRatingModal
        lead={ratingLead}
        open={ratingLead !== null}
        handleClose={() => setRatingLead(null)}
      />
      <StopFactorsModal
        owner={
          stopFactorsLead && {
            type: "lead",
            id: stopFactorsLead.id,
            customerId,
            name: stopFactorsLead.borrowerName,
            inn: stopFactorsLead.inn,
          }
        }
        open={stopFactorsLead !== null}
        onClose={() => setStopFactorsLead(null)}
        onRecalculated={(report) =>
          setLeads((prev) =>
            prev.map((l) =>
              l.id === stopFactorsLead.id
                ? { ...l, triggeredStopFactors: report.triggeredCount }
                : l
            )
          )
        }
      />
      <EditLeadModal
        lead={editLead}
        open={editLead !== null}
        handleClose={() => setEditLead(null)}
        onSaved={(updated) => {
          setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
          setEditLead(null);
        }}
      />
      <ConfirmModal
        open={deletedId !== undefined}
        onClose={handleCloseModal}
        onConfirm={handleDelete}
        title={t("archiveModal.title")}
        text={t("archiveModal.text")}
        confirmLabel={t("archiveModal.button2")}
        cancelLabel={t("archiveModal.button1")}
      />
      <ConfirmModal
        open={convertTarget !== null}
        onClose={(e) => { e.preventDefault(); setConvertTarget(null); }}
        onConfirm={handleConvert}
        title={t("convertLeadModal.title")}
        text={t("convertLeadModal.text")}
        confirmLabel={t("convertLeadModal.button2")}
        cancelLabel={t("convertLeadModal.button1")}
      />
    </React.Fragment>
  );
};

export default connect(mapStateToProps)(LeadsTable);
